import React, { useCallback, useEffect, useState } from 'react';
import {
    CheckCircle, AlertTriangle, Plus, Trash2, Tag, Mail, MailCheck, Send, Save, Users, Boxes,
} from 'lucide-react';
import api from '../../api';
import HtmlEditor from '../../components/HtmlEditor';
import Spinner from '../../components/Spinner';

const inputClass = "px-3.5 py-2.5 border border-gray-300 rounded-lg text-[15px] transition-all bg-white focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10";
const primaryBtn = "bg-blue-600 text-white px-4 py-2.5 border-none rounded-lg text-sm font-medium transition-colors flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed";
const secondaryBtn = "bg-white text-gray-700 px-4 py-2.5 border border-gray-300 rounded-lg text-sm font-medium transition-all flex items-center gap-2 enabled:cursor-pointer enabled:hover:bg-gray-50 enabled:hover:border-gray-400 disabled:opacity-50 disabled:cursor-not-allowed";
const cardClass = "bg-white rounded-xl p-6 mb-6 shadow-sm border border-gray-200";

/** Разметка нового приветствия: не пустое поле, а письмо, которое уже можно отправить. */
const STARTER_HTML = `<h2 style="margin:0 0 18px; font-size:21px; line-height:28px; color:#111827;">Добро пожаловать!</h2>
<p style="margin:0 0 14px; font-size:15px; line-height:23px; color:#374151;">
  Здравствуйте, {{name}}! Вы впервые вошли в сервис {{serviceName}} — рады видеть вас.
</p>
<p style="margin:0 0 14px; font-size:15px; line-height:23px; color:#374151;">
  Аккаунт IT-Burger уже работает во всех наших сервисах: отдельная регистрация нигде не нужна.
</p>`;

const AdminTagsPage = () => {
    const [tags, setTags] = useState([]);
    const [senders, setSenders] = useState([]);
    const [variables, setVariables] = useState([]);
    const [alert, setAlert] = useState(null);
    const [newTag, setNewTag] = useState({ name: '', description: '' });

    // Метка, приветствие которой сейчас правят.
    const [selected, setSelected] = useState(null);
    const [form, setForm] = useState(null);
    const [loadingWelcome, setLoadingWelcome] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    const showAlert = (type, message) => {
        setAlert({ type, message });
        setTimeout(() => setAlert(null), 6000);
    };

    const errorText = (err, fallback) =>
        err?.response?.data?.error || err?.response?.data?.message
        || (typeof err?.response?.data === 'string' && err.response.data) || fallback;

    const fetchTags = useCallback(() => {
        api.get('/api/admin/tags')
            .then(res => setTags(res.data))
            .catch(() => showAlert('error', 'Не удалось загрузить метки'));
    }, []);

    useEffect(() => {
        fetchTags();
        api.get('/api/admin/mail/senders').then(res => setSenders(res.data)).catch(() => setSenders([]));
    }, [fetchTags]);

    const openWelcome = (tag) => {
        setSelected(tag);
        setForm(null);
        setPreview(null);
        setLoadingWelcome(true);

        Promise.all([
            api.get(`/api/admin/tags/${tag.id}/welcome`),
            variables.length ? Promise.resolve({ data: variables }) : api.get(`/api/admin/tags/${tag.id}/welcome/variables`),
        ])
            .then(([welcome, vars]) => {
                setVariables(vars.data);
                // 204 - письма ещё нет: показываем заготовку, а не пустое поле.
                setForm(welcome.data && welcome.data.subject ? {
                    senderId: welcome.data.senderId ?? '',
                    subject: welcome.data.subject,
                    bodyHtml: welcome.data.bodyHtml,
                    enabled: welcome.data.enabled,
                } : {
                    senderId: '',
                    subject: `Добро пожаловать в ${tag.name}`,
                    bodyHtml: STARTER_HTML,
                    enabled: false,
                });
            })
            .catch(() => showAlert('error', 'Не удалось загрузить приветственное письмо'))
            .finally(() => setLoadingWelcome(false));
    };

    const payload = () => ({
        senderId: form.senderId === '' ? null : Number(form.senderId),
        subject: form.subject,
        bodyHtml: form.bodyHtml,
        enabled: form.enabled,
    });

    const handleSave = () => {
        setSaving(true);
        api.put(`/api/admin/tags/${selected.id}/welcome`, payload())
            .then(() => {
                showAlert('success', form.enabled
                    ? 'Письмо сохранено и включено'
                    : 'Письмо сохранено как черновик — оно не отправляется, пока выключено');
                fetchTags();
            })
            .catch(err => showAlert('error', errorText(err, 'Не удалось сохранить письмо')))
            .finally(() => setSaving(false));
    };

    const handlePreview = () => {
        setPreviewLoading(true);
        api.post(`/api/admin/tags/${selected.id}/welcome/preview`, payload())
            .then(res => setPreview(res.data))
            .catch(err => {
                setPreview(null);
                showAlert('error', errorText(err, 'Не удалось собрать письмо'));
            })
            .finally(() => setPreviewLoading(false));
    };

    const handleTest = () => {
        setTesting(true);
        api.post(`/api/admin/tags/${selected.id}/welcome/test`, payload())
            .then(() => showAlert('success', 'Проверочное письмо отправлено на вашу почту'))
            .catch(err => showAlert('error', errorText(err, 'Не удалось отправить письмо')))
            .finally(() => setTesting(false));
    };

    const handleDeleteWelcome = () => {
        if (!window.confirm(`Удалить приветственное письмо метки «${selected.name}»? Вёрстка будет потеряна.`)) return;
        api.delete(`/api/admin/tags/${selected.id}/welcome`)
            .then(() => {
                setSelected(null);
                setForm(null);
                fetchTags();
                showAlert('success', 'Письмо удалено');
            })
            .catch(err => showAlert('error', errorText(err, 'Не удалось удалить письмо')));
    };

    const handleCreateTag = () => {
        if (!newTag.name.trim()) {
            showAlert('error', 'Укажите название метки');
            return;
        }
        api.post('/api/admin/tags', { name: newTag.name.trim(), description: newTag.description.trim() || null })
            .then(() => {
                setNewTag({ name: '', description: '' });
                fetchTags();
                showAlert('success', 'Метка создана');
            })
            .catch(err => showAlert('error', errorText(err, 'Не удалось создать метку')));
    };

    const handleDeleteTag = (tag) => {
        const details = [];
        if (tag.clientCount) details.push(`клиентов: ${tag.clientCount}`);
        if (tag.userCount) details.push(`пользователей: ${tag.userCount}`);
        const tail = details.length ? ` Она сейчас у ${details.join(', ')}.` : '';
        if (!window.confirm(`Удалить метку «${tag.name}»?${tail} Приветственное письмо тоже пропадёт.`)) return;

        api.delete(`/api/admin/tags/${tag.id}`)
            .then(() => {
                if (selected?.id === tag.id) { setSelected(null); setForm(null); }
                fetchTags();
                showAlert('success', 'Метка удалена');
            })
            .catch(err => showAlert('error', errorText(err, 'Не удалось удалить метку')));
    };

    return (
        <div>
            <div className="mb-8">
                <h1 className="text-[28px] font-semibold text-gray-900 m-0">Метки сервисов</h1>
                <p className="text-gray-500 text-sm mt-2">
                    Метка достаётся пользователю при первом входе в клиента, за которым она закреплена,
                    и держится год с последнего входа. По меткам видно, кто каким сервисом пользуется,
                    по ним же фильтруются рассылки — и в момент первого проставления уходит
                    приветственное письмо сервиса.
                </p>
            </div>

            {alert && (
                <div className={`p-3 rounded-lg mb-5 text-sm flex items-start gap-2.5 ${alert.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                    {alert.type === 'success' ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
                    <span>{alert.message}</span>
                </div>
            )}

            <div className={cardClass}>
                <h3 className="mt-0 mb-5 text-lg font-semibold text-gray-900">Метки</h3>

                {tags.length === 0 ? (
                    <p className="text-sm text-gray-500 m-0 mb-5">Меток пока нет.</p>
                ) : (
                    <ul className="list-none p-0 m-0 mb-5">
                        {tags.map(tag => (
                            <li
                                key={tag.id}
                                className={`flex items-center justify-between gap-4 py-3.5 border-b border-gray-100 last:border-b-0 ${
                                    selected?.id === tag.id ? 'bg-blue-50/40 -mx-2 px-2 rounded-lg' : ''}`}
                            >
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Tag size={14} className="text-blue-600 shrink-0" />
                                        <span className="font-medium text-gray-900">{tag.name}</span>
                                        {tag.welcomeConfigured && (
                                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${
                                                tag.welcomeEnabled
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                    : 'bg-gray-100 text-gray-600 border-gray-200'}`}>
                                                <MailCheck size={11} />
                                                {tag.welcomeEnabled ? 'приветствие включено' : 'черновик приветствия'}
                                            </span>
                                        )}
                                    </div>
                                    {tag.description && (
                                        <div className="text-[13px] text-gray-500 truncate mt-0.5">{tag.description}</div>
                                    )}
                                    <div className="text-xs text-gray-400 mt-1 flex items-center gap-3">
                                        <span className="flex items-center gap-1"><Boxes size={11} /> клиентов: {tag.clientCount ?? 0}</span>
                                        <span className="flex items-center gap-1"><Users size={11} /> пользователей: {tag.userCount ?? 0}</span>
                                    </div>
                                </div>
                                <div className="flex gap-2 shrink-0">
                                    <button className={secondaryBtn} onClick={() => openWelcome(tag)}>
                                        <Mail size={14} /> Приветствие
                                    </button>
                                    <button
                                        className="bg-transparent text-gray-400 p-2 border border-transparent rounded-md cursor-pointer transition-all hover:text-red-600 hover:border-red-200 hover:bg-red-50"
                                        title="Удалить метку"
                                        onClick={() => handleDeleteTag(tag)}
                                    >
                                        <Trash2 size={16} />
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                    <input
                        className={inputClass + ' sm:w-[220px]'}
                        placeholder="Название метки"
                        value={newTag.name}
                        onChange={e => setNewTag({ ...newTag, name: e.target.value })}
                    />
                    <input
                        className={inputClass + ' flex-1'}
                        placeholder="Пояснение (необязательно)"
                        value={newTag.description}
                        onChange={e => setNewTag({ ...newTag, description: e.target.value })}
                    />
                    <button className={primaryBtn + ' shrink-0'} onClick={handleCreateTag}>
                        <Plus size={16} /> Добавить
                    </button>
                </div>
            </div>

            {selected && (
                <div className={cardClass}>
                    <div className="flex items-start justify-between mb-1 gap-4">
                        <h3 className="m-0 text-lg font-semibold text-gray-900">
                            Приветствие метки «{selected.name}»
                        </h3>
                        <button
                            className="bg-transparent border-none text-gray-400 text-sm cursor-pointer hover:text-gray-700"
                            onClick={() => { setSelected(null); setForm(null); }}
                        >
                            Закрыть
                        </button>
                    </div>
                    <p className="text-gray-500 text-sm mb-5">
                        Уходит один раз — когда пользователю впервые проставили эту метку, то есть когда
                        он впервые воспользовался сервисом. Повторные входы письма не повторяют.
                    </p>

                    {loadingWelcome && (
                        <div className="flex items-center gap-3 text-gray-500 py-6">
                            <Spinner size={20} /> Загружаем письмо…
                        </div>
                    )}

                    {form && !loadingWelcome && (
                        <>
                            <div className="mb-4 flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-gray-700">От кого</label>
                                <select className={inputClass} value={form.senderId}
                                        onChange={e => setForm({ ...form, senderId: e.target.value })}>
                                    <option value="">Системный адрес сервиса авторизации</option>
                                    {senders.filter(sender => sender.enabled).map(sender => (
                                        <option key={sender.id} value={sender.id}>
                                            {sender.name} ({sender.fromEmail})
                                        </option>
                                    ))}
                                </select>
                                <span className="text-[13px] text-gray-500">
                                    Приветствие сервиса логично отправлять с адреса этого сервиса — тогда
                                    оно и выглядит как письмо от него.
                                </span>
                            </div>

                            <div className="mb-4 flex flex-col gap-1.5">
                                <label className="text-sm font-medium text-gray-700">Тема письма</label>
                                <input className={inputClass} value={form.subject}
                                       onChange={e => setForm({ ...form, subject: e.target.value })} />
                                <span className="text-[13px] text-gray-500">
                                    Данные пользователя подставляются и здесь: «Добро пожаловать, {'{{name}}'}».
                                </span>
                            </div>

                            <div className="mb-4">
                                <label className="text-sm font-medium text-gray-700 block mb-1.5">Письмо</label>
                                <HtmlEditor
                                    value={form.bodyHtml}
                                    onChange={html => setForm(f => ({ ...f, bodyHtml: html }))}
                                    onPreview={handlePreview}
                                    previewHtml={preview}
                                    previewLoading={previewLoading}
                                    variables={variables}
                                />
                            </div>

                            <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer mb-5">
                                <input type="checkbox" className="mt-1" checked={form.enabled}
                                       onChange={e => setForm({ ...form, enabled: e.target.checked })} />
                                <span>
                                    Отправлять новым пользователям сервиса
                                    <span className="block text-[13px] text-gray-500">
                                        Выключено — письмо остаётся черновиком: метка проставляется, письмо не уходит.
                                    </span>
                                </span>
                            </label>

                            <div className="flex gap-3 flex-wrap">
                                <button className={primaryBtn} onClick={handleSave} disabled={saving}>
                                    {saving ? <Spinner size={16} /> : <Save size={16} />}
                                    {saving ? 'Сохраняем…' : 'Сохранить'}
                                </button>
                                <button className={secondaryBtn} onClick={handleTest} disabled={testing}>
                                    {testing ? <Spinner size={16} /> : <Send size={16} />}
                                    Отправить себе
                                </button>
                                {selected.welcomeConfigured && (
                                    <button
                                        className="bg-transparent text-red-500 px-4 py-2.5 border border-red-200 rounded-lg text-sm font-medium cursor-pointer transition-all hover:bg-red-50 hover:border-red-400 flex items-center gap-2"
                                        onClick={handleDeleteWelcome}
                                    >
                                        <Trash2 size={16} /> Удалить письмо
                                    </button>
                                )}
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminTagsPage;
